import React from "react";

const A = "/assets/mobile/footer";

const SectionTitle = ({ children }) => (
  <span className="block font-['Outfit'] font-bold text-[14px] leading-normal tracking-[2px] uppercase text-[#473fdc]">{children}</span>
);

const ContactItem = ({ icon, title, children }) => (
  <div className="flex gap-[12px] items-start w-full">
    <img src={`${A}/${icon}`} alt="" width="32" height="32" className="shrink-0 size-[32px]" />
    <div className="flex flex-1 min-w-0 flex-col gap-[4px] items-start text-left font-['Outfit']">
      <span className="font-bold text-[11px] leading-normal tracking-[1px] uppercase text-[#9bb0a5] whitespace-nowrap">{title}</span>
      {children}
    </div>
  </div>
);

// Mobile (<768px) footer. Links, cities and navigation handlers come from Footer.
const FooterMob = ({ cities, quickLinks, onCityClick, onQuickLinkClick, appStoreUrl, playStoreUrl, socials, contact }) => (
  <footer className="md:hidden relative z-10 -mt-20 bg-white border border-[#b4b4b4] rounded-t-[32px] overflow-hidden">
    <div className="flex flex-col px-[19px] pt-[23px] font-['Outfit'] text-left">
      {/* Brand */}
      <div className="flex flex-col gap-[24px] items-start">
        <img src={`${A}/logo.svg`} alt="Wowcarz Logo" width="135" height="52" className="w-[135px] h-[52px]" />
        <span className="block font-normal text-[14px] leading-[20px] tracking-[0.4px] text-[#808080]">
          We have been proudly delivering reliable Self Drive car rental services since August 2016. At Wowcarz, our mission is to make every travel plan seamless and stress-free.
        </span>
        <div className="flex gap-[8px] items-start">
          <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store" className="relative block w-[132.22px] h-[42.95px]">
            <img src={`${A}/app-store.svg`} alt="Download on the App Store" className="absolute inset-0 block max-w-none size-full" />
          </a>
          <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play" className="relative block w-[138.39px] h-[42.95px] rounded-[8px] bg-white border-[1.2px] border-[#c1bdfd]">
            <img src={`${A}/play-store.svg`} alt="Get it on Google Play" className="absolute max-w-none left-[13.87px] top-[8.49px] w-[108.57px] h-[24.87px]" />
          </a>
        </div>
      </div>

      {/* Cities */}
      <div className="mt-[32px] min-h-[117px] flex flex-col gap-[16px]">
        <SectionTitle>Available Cities</SectionTitle>
        <div className="flex flex-wrap gap-[8px] items-start">
          {cities.map((city) => (
            <button
              key={city.slug}
              onClick={() => onCityClick(city)}
              className="px-[14px] py-[8px] rounded-[20px] border border-[#c1bdfd] font-medium text-[13px] leading-normal text-[#100e0e] whitespace-nowrap"
            >
              {city.name}
            </button>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-[16px] min-h-[97px] flex flex-col gap-[16px]">
        <SectionTitle>Quick Links</SectionTitle>
        <div className="flex flex-wrap gap-x-[22px] gap-y-[18px] items-start">
          {quickLinks.map((link) => {
            const content = (
              <>
                <img src={`${A}/chevron-right.svg`} alt="" width="10" height="10" className="shrink-0 size-[10px]" />
                <span className="font-medium text-[15px] leading-normal text-[#989898] whitespace-nowrap">{link.label || link.name}</span>
              </>
            );
            return link.external ? (
              <a key={link.name} href={link.path} target="_blank" rel="noopener noreferrer" className="flex gap-[8px] items-center">
                {content}
              </a>
            ) : (
              <button key={link.name} onClick={() => onQuickLinkClick(link)} className="flex gap-[8px] items-center">
                {content}
              </button>
            );
          })}
        </div>
      </div>

      {/* Contact */}
      <div className="mt-[16px] flex flex-col gap-[16px]">
        <SectionTitle>Get In Touch</SectionTitle>
        <div className="flex flex-col gap-[20px] items-start w-full">
          <ContactItem icon="icon-address.svg" title="Address">
            <a href={contact.mapUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-[13px] leading-[1.4] text-[#100e0e]">
              {contact.address}
            </a>
          </ContactItem>
          <ContactItem icon="icon-phone.svg" title="Phone">
            <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="font-medium text-[13px] leading-[1.4] text-[#100e0e]">
              {contact.phone}
            </a>
          </ContactItem>
          <ContactItem icon="icon-email.svg" title="Email Addresses">
            <div className="flex flex-col gap-[4px] items-start w-full">
              {contact.emails.map((email) => (
                <a key={email} href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`} target="_blank" rel="noopener noreferrer" className="font-medium text-[13px] leading-normal text-[#100e0e]">
                  {email}
                </a>
              ))}
            </div>
          </ContactItem>
        </div>
      </div>
    </div>

    {/* Skyline with watermark behind it */}
    <div className="relative mt-[-60px] h-[231px] pointer-events-none">
      <span
        className="absolute left-1/2 top-[92.3px] -translate-x-1/2 whitespace-nowrap opacity-35 text-[#f4bbbb] text-[59.41px] leading-normal tracking-[13.59px] font-extrabold select-none"
        style={{ fontFamily: "Inter, 'Outfit', sans-serif" }}
        aria-hidden="true"
      >
        WOWCARZ
      </span>
      <div className="absolute inset-0 overflow-hidden">
        <img src={`${A}/skyline.png`} alt="Wowcarz cities" className="absolute max-w-none h-[185.68%] w-[100.11%] left-[-0.06%] top-[-48.02%]" />
      </div>
    </div>

    {/* Bottom bar */}
    <div className="flex flex-col items-center pt-[5px] pb-[27px] gap-[19px]">
      <div className="flex gap-[42px] items-center">
        {socials.map((s) => (
          <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name} className="size-[36px] shrink-0">
            <img src={`${A}/social-${s.name.toLowerCase()}.svg`} alt={s.name} className="block size-full" />
          </a>
        ))}
      </div>
      <span className="font-['Outfit'] font-medium text-[13px] leading-normal text-center text-[#808080]">
        Copyright, Wow Carz {new Date().getFullYear()}. All rights reserved.
      </span>
    </div>
  </footer>
);

export default FooterMob;
